import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MenuCity {
  /** Stable id (office id when sourced from offices, else slug) */
  id: string;
  /** Display label, e.g. "New York" */
  label: string;
  /** Canonical city used to filter the roster, e.g. "New York" */
  city: string;
  /** When true, hidden from the public menu */
  hidden?: boolean;
}

export type SocialPlatform =
  | "instagram" | "tiktok" | "youtube" | "vimeo"
  | "x" | "facebook" | "linkedin" | "pinterest" | "spotify";

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
}

export interface SubmissionFieldConfig {
  enabled: boolean;
  required: boolean;
}

export interface CustomSubmissionField {
  key: string;
  label: string;
  required: boolean;
}

export interface SubmissionsFields {
  full_name?: SubmissionFieldConfig;
  email?: SubmissionFieldConfig;
  phone?: SubmissionFieldConfig;
  instagram_handle?: SubmissionFieldConfig;
  height?: SubmissionFieldConfig;
  bio?: SubmissionFieldConfig;
  custom?: CustomSubmissionField[];
}

export type SiteThemeKey = "studio" | "paper" | "noir";

export interface AgencyWebsiteRow {
  agency_id: string;
  enabled: boolean;
  custom_domain: string | null;
  tagline: string | null;
  hero_image_url: string | null;
  hero_video_url: string | null;
  logo_url: string | null;
  primary_color: string;
  contact_email: string | null;
  featured_model_ids: string[];
  about: string | null;
  about_title: string | null;
  about_image_url: string | null;
  socials: SocialLink[];
  menu_cities: MenuCity[];
  menu_show_instagram: boolean;
  submissions_enabled: boolean;
  submissions_email: string | null;
  submissions_intro: string | null;
  submissions_fields: SubmissionsFields;
  news_enabled: boolean;
  terms_content: string | null;
  terms_pdf_url: string | null;
  hero_headline: string | null;
  hero_font: "serif" | "sans" | "mono";
  hero_position:
    | "top-left" | "top-center" | "top-right"
    | "center-left" | "center" | "center-right"
    | "bottom-left" | "bottom-center" | "bottom-right";
  hero_text_color: "light" | "dark";
  theme: SiteThemeKey;
  home_modules: HomeModuleKey[];
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  // Phase 2 - Style customizer
  font_display_key: string | null;
  font_body_key: string | null;
  secondary_color: string | null;
  radius_px: number | null;
  section_padding_px: number | null;
  heading_case: "normal" | "uppercase" | null;
  hero_weight: number | null;
  custom_css: string | null;
  section_variants: SectionVariants;
  page_overrides: PageOverrides;
  media_meta: MediaMeta;
  // Phase 4 - Trust & distribution
  favicon_url: string | null;
  ga4_measurement_id: string | null;
  plausible_domain: string | null;
  // password gate (hash is server-only; the public site only needs to know if one is set)
  has_preview_password?: boolean;
  // Custom-coded theme layer (designer-authored HTML with live Focus tokens)
  custom_html_enabled: boolean;
  custom_header_html: string | null;
  custom_footer_html: string | null;
  custom_home_html: string | null;
  /** Full designer-authored document rendered in a sandboxed shell */
  custom_full_html: string | null;
  custom_full_html_enabled: boolean;
}

export interface SectionVariants {
  hero?: "full-bleed" | "split" | "type-only" | "video-loop";
  about?: "image-right" | "image-left" | "image-top" | "no-image";
  news?: "grid" | "list" | "magazine";
}

/** Override built-in page metadata (label/hidden/per-page SEO). Keyed by built-in slug. */
export type BuiltinPageSlug = "about" | "news" | "contact" | "get-scouted" | "models" | "terms";
export interface PageOverride {
  nav_label?: string | null;
  hidden?: boolean;
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;
}
export type PageOverrides = Partial<Record<BuiltinPageSlug, PageOverride>>;

/** Per-image metadata keyed by storage path. */
export interface ImageMeta {
  focal_x?: number; // 0..1
  focal_y?: number; // 0..1
}
export type MediaMeta = Record<string, ImageMeta>;

export type HomeModuleKey = "hero" | "featured" | "about" | "news" | "contact";

export const DEFAULT_HOME_MODULES: HomeModuleKey[] = ["hero", "featured", "about", "news", "contact"];

export interface AgencyCore {
  id: string;
  name: string;
  public_handle: string | null;
  logo_url: string | null;
  primary_color: string | null;
  bio: string | null;
  city: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  instagram: string | null;
}

export interface AgencySiteData {
  agency: AgencyCore;
  website: AgencyWebsiteRow;
}

const WEBSITE_DEFAULTS = {
  hero_video_url: null as string | null,
  menu_cities: [] as MenuCity[],
  menu_show_instagram: true,
  submissions_enabled: true,
  terms_content: null as string | null,
  terms_pdf_url: null as string | null,
};

function normalizeWebsite(row: Record<string, unknown>): AgencyWebsiteRow {
  return {
    ...(row as unknown as AgencyWebsiteRow),
    hero_video_url: (row.hero_video_url as string | null) ?? null,
    logo_url: (row.logo_url as string | null) ?? null,
    about: (row.about as string | null) ?? null,
    about_title: (row.about_title as string | null) ?? null,
    about_image_url: (row.about_image_url as string | null) ?? null,
    socials: ((row.socials as SocialLink[] | null) ?? []) as SocialLink[],
    menu_cities: (row.menu_cities as MenuCity[] | null) ?? [],
    menu_show_instagram: (row.menu_show_instagram as boolean | undefined) ?? true,
    submissions_enabled: (row.submissions_enabled as boolean | undefined) ?? true,
    submissions_email: (row.submissions_email as string | null) ?? null,
    submissions_intro: (row.submissions_intro as string | null) ?? null,
    submissions_fields: ((row.submissions_fields as SubmissionsFields | null) ?? {}) as SubmissionsFields,
    news_enabled: (row.news_enabled as boolean | undefined) ?? false,
    terms_content: (row.terms_content as string | null) ?? null,
    terms_pdf_url: (row.terms_pdf_url as string | null) ?? null,
    hero_headline: (row.hero_headline as string | null) ?? null,
    hero_font: ((row.hero_font as string | undefined) ?? "serif") as AgencyWebsiteRow["hero_font"],
    hero_position: ((row.hero_position as string | undefined) ?? "bottom-left") as AgencyWebsiteRow["hero_position"],
    hero_text_color: ((row.hero_text_color as string | undefined) ?? "light") as AgencyWebsiteRow["hero_text_color"],
    theme: ((row.theme as string | undefined) ?? "studio") as SiteThemeKey,
    home_modules: normalizeHomeModules(row.home_modules),
    seo_title: (row.seo_title as string | null) ?? null,
    seo_description: (row.seo_description as string | null) ?? null,
    og_image_url: (row.og_image_url as string | null) ?? null,
    font_display_key: (row.font_display_key as string | null) ?? null,
    font_body_key: (row.font_body_key as string | null) ?? null,
    secondary_color: (row.secondary_color as string | null) ?? null,
    radius_px: (row.radius_px as number | null) ?? null,
    section_padding_px: (row.section_padding_px as number | null) ?? null,
    heading_case: ((row.heading_case as string | null) ?? null) as AgencyWebsiteRow["heading_case"],
    hero_weight: (row.hero_weight as number | null) ?? null,
    custom_css: (row.custom_css as string | null) ?? null,
    section_variants: ((row.section_variants as SectionVariants | null) ?? {}) as SectionVariants,
    page_overrides: ((row.page_overrides as PageOverrides | null) ?? {}) as PageOverrides,
    media_meta: ((row.media_meta as MediaMeta | null) ?? {}) as MediaMeta,
    favicon_url: (row.favicon_url as string | null) ?? null,
    ga4_measurement_id: (row.ga4_measurement_id as string | null) ?? null,
    plausible_domain: (row.plausible_domain as string | null) ?? null,
    has_preview_password:
      (row.has_preview_password as boolean | undefined) ??
      !!(row.preview_password_hash as string | null),
    custom_html_enabled: (row.custom_html_enabled as boolean | null) ?? false,
    custom_header_html: (row.custom_header_html as string | null) ?? null,
    custom_footer_html: (row.custom_footer_html as string | null) ?? null,
    custom_home_html: (row.custom_home_html as string | null) ?? null,
    custom_full_html: (row.custom_full_html as string | null) ?? null,
    custom_full_html_enabled: (row.custom_full_html_enabled as boolean | undefined) ?? false,
  };
}

function normalizeHomeModules(input: unknown): HomeModuleKey[] {
  const allowed: HomeModuleKey[] = ["hero", "featured", "about", "news", "contact"];
  const arr = Array.isArray(input) ? (input as unknown[]) : [];
  const cleaned = arr
    .filter((k): k is HomeModuleKey => typeof k === "string" && (allowed as string[]).includes(k))
    .filter((k, i, a) => a.indexOf(k) === i);
  return cleaned.length ? cleaned : DEFAULT_HOME_MODULES;
}

function isPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("preview") === "1";
  } catch {
    return false;
  }
}

async function fetchByHandle(handle: string): Promise<AgencySiteData | null> {
  const { data: agency } = await supabase
    .from("agencies")
    .select("id, name, public_handle, logo_url, primary_color, bio, city, website, email, phone, instagram, is_published, approval_status")
    .eq("public_handle", handle.toLowerCase())
    .maybeSingle();
  if (!agency) return null;
  const preview = isPreviewMode();
  // Preview mode requires the authenticated owner and needs draft_data,
  // so it reads the base table (RLS allows owner access). Public visitors
  // read the column-restricted public view.
  const source = preview ? "agency_websites" : "agency_websites_public";
  let query = supabase
    .from(source as "agency_websites")
    .select("*")
    .eq("agency_id", agency.id);
  if (!preview) query = query.eq("enabled", true);
  const { data: website } = await query.maybeSingle();
  if (!website) return null;
  return { agency: agency as AgencyCore, website: normalizeWebsite(website as Record<string, unknown>) };
}

async function fetchByCustomDomain(domain: string): Promise<AgencySiteData | null> {
  const host = domain.toLowerCase();
  const alt = host.startsWith("www.") ? host.slice(4) : `www.${host}`;
  const { data: websites } = await supabase
    .from("agency_websites_public")
    .select("*")
    .in("custom_domain", [host, alt])
    .eq("enabled", true)
    .limit(2);
  const website =
    (websites ?? []).find((w) => (w.custom_domain ?? "").toLowerCase() === host) ??
    (websites ?? [])[0];
  if (!website) return null;
  const { data: agency } = await supabase
    .from("agencies")
    .select("id, name, public_handle, logo_url, primary_color, bio, city, website, email, phone, instagram")
    .eq("id", website.agency_id)
    .maybeSingle();
  if (!agency) return null;
  return { agency: agency as AgencyCore, website: normalizeWebsite(website as Record<string, unknown>) };
}

export function useAgencySiteByHandle(handle: string | undefined) {
  const preview = isPreviewMode();
  return useQuery({
    queryKey: ["agency-site", "handle", handle, preview ? "preview" : "live"],
    enabled: !!handle,
    queryFn: () => fetchByHandle(handle!),
    staleTime: preview ? 0 : 60_000,
  });
}

export function useAgencySiteByDomain(domain: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "domain", domain],
    enabled: !!domain,
    queryFn: () => fetchByCustomDomain(domain!),
    staleTime: 60_000,
  });
}

export function useAgencySiteDivisions(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "divisions", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_divisions")
        .select("id, name, display_order, is_visible")
        .eq("agency_id", agencyId!)
        .eq("is_visible", true)
        .order("display_order", { ascending: true });
      return data ?? [];
    },
  });
}

export interface SiteOffice {
  id: string;
  name: string;
  city: string | null;
}

export function useAgencySiteOffices(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "offices", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_offices")
        .select("id, name, city")
        .eq("agency_id", agencyId!)
        .order("created_at", { ascending: true });
      return (data ?? []) as SiteOffice[];
    },
  });
}

export interface SiteModel {
  id: string;
  name: string;
  public_handle: string | null;
  profile_image_url: string | null;
  gender: string | null;
  height: string | null;
  bust: string | null;
  waist: string | null;
  hips: string | null;
  shoe_size: string | null;
  hair_color: string | null;
  eye_color: string | null;
  bio: string | null;
  instagram_handle: string | null;
  location_city: string | null;
}

export function useAgencySiteModels(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "models", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("models")
        .select("id, name, public_handle, profile_image_url, gender, height, bust, waist, hips, shoe_size, hair_color, eye_color, bio, instagram_handle, location_city")
        .eq("agency_id", agencyId!)
        .eq("is_published", true)
        .order("name", { ascending: true });
      return (data ?? []) as SiteModel[];
    },
  });
}

export function useAgencySiteModelDivisions(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "model-divisions", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: divs } = await supabase
        .from("agency_divisions")
        .select("id")
        .eq("agency_id", agencyId!);
      const ids = (divs ?? []).map((d) => d.id);
      if (ids.length === 0) return {} as Record<string, string[]>;
      const { data } = await supabase
        .from("model_divisions")
        .select("model_id, division_id")
        .in("division_id", ids);
      const byModel: Record<string, string[]> = {};
      for (const row of data ?? []) {
        const mid = (row as { model_id: string }).model_id;
        const did = (row as { division_id: string }).division_id;
        (byModel[mid] ||= []).push(did);
      }
      return byModel;
    },
  });
}

// ---- Public-site additions: contact, departments, news ----

export interface PublicOffice {
  id: string;
  name: string;
  city: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
}

export function usePublicAgencyOffices(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "public-offices", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_offices")
        .select("id, name, city, address, email, phone, sort_order")
        .eq("agency_id", agencyId!)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      return ((data ?? []) as unknown as (PublicOffice & { sort_order: number })[])
        .map(({ sort_order: _s, ...rest }) => rest);
    },
  });
}

export interface PublicDepartment {
  id: string;
  label: string;
  email: string;
}

export function usePublicAgencyDepartments(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "public-departments", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_departments")
        .select("id, label, email")
        .eq("agency_id", agencyId!)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      return (data ?? []) as PublicDepartment[];
    },
  });
}

export interface GallerySummary {
  id: string;
  slug: string;
  title: string;
  cover_image_url: string | null;
  published_at: string | null;
  featured_model_id: string | null;
  featured_model_name: string | null;
}

export interface Gallery extends GallerySummary {
  images: string[];
}

// Backwards-compatible aliases.
export type NewsPostSummary = GallerySummary;
export type NewsPost = Gallery;

interface RawGalleryRow {
  id: string;
  slug: string;
  title: string;
  cover_image_url: string | null;
  images: string[] | null;
  published_at: string | null;
  publish_at: string | null;
  featured_model_id: string | null;
  featured_model: { name: string } | null;
}

const GALLERY_SELECT =
  "id, slug, title, cover_image_url, images, published_at, publish_at, featured_model_id, featured_model:models!agency_news_posts_featured_model_id_fkey(name)";

function mapGallerySummary(r: RawGalleryRow): GallerySummary {
  const images = Array.isArray(r.images) ? r.images : [];
  return {
    id: r.id,
    slug: r.slug,
    title: r.title,
    cover_image_url: r.cover_image_url ?? images[0] ?? null,
    published_at: r.published_at,
    featured_model_id: r.featured_model_id,
    featured_model_name: r.featured_model?.name ?? null,
  };
}

export function usePublicAgencyNews(agencyId: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "public-news", agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data } = await supabase
        .from("agency_news_posts")
        .select(GALLERY_SELECT)
        .eq("agency_id", agencyId!)
        .eq("published", true)
        .or(`publish_at.is.null,publish_at.lte.${nowIso}`)
        .order("published_at", { ascending: false });
      return ((data ?? []) as unknown as RawGalleryRow[]).map(mapGallerySummary);
    },
  });
}

export function usePublicAgencyNewsPost(agencyId: string | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ["agency-site", "public-news-post", agencyId, slug],
    enabled: !!agencyId && !!slug,
    staleTime: 60_000,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data } = await supabase
        .from("agency_news_posts")
        .select(GALLERY_SELECT)
        .eq("agency_id", agencyId!)
        .eq("slug", slug!)
        .eq("published", true)
        .or(`publish_at.is.null,publish_at.lte.${nowIso}`)
        .maybeSingle();
      if (!data) return null;
      const r = data as unknown as RawGalleryRow;
      const summary = mapGallerySummary(r);
      return { ...summary, images: Array.isArray(r.images) ? r.images : [] } satisfies Gallery;
    },
  });
}
